'use client';

export type AssistantMapViewport = {
  north: number;
  south: number;
  east: number;
  west: number;
  centerLatitude: number;
  centerLongitude: number;
  zoom: number;
};

export type AssistantMapItem = {
  id?: string;
  kind?: string;
  name?: string;
  latitude: number;
  longitude: number;
  meta?: string;
};

export type AssistantMapContext = {
  viewport: AssistantMapViewport;
  selected_item_id?: string;
  visible_items: AssistantMapItem[];
  observed_at: string;
};

export type AssistantDataChangedDetail = {
  tripSlug: string;
  tools: string[];
};

type PublishMarker = {
  id?: string;
  kind?: string;
  label?: string;
  popupMeta?: string;
  latitude: number;
  longitude: number;
};

// Enough spatial context to understand what the user sees without bloating every Gemini prompt.
const MAX_VISIBLE_ITEMS = 10;
let latestMapContext: AssistantMapContext | null = null;

function insideLongitude(longitude: number, west: number, east: number): boolean {
  if (west <= east) return longitude >= west && longitude <= east;
  return longitude >= west || longitude <= east;
}

function visible(marker: PublishMarker, viewport: AssistantMapViewport): boolean {
  return Number.isFinite(marker.latitude)
    && Number.isFinite(marker.longitude)
    && marker.latitude >= viewport.south
    && marker.latitude <= viewport.north
    && insideLongitude(marker.longitude, viewport.west, viewport.east);
}

function clean(value: string | undefined, max: number): string | undefined {
  const next = value?.replace(/\s+/g, ' ').trim();
  return next ? next.slice(0, max) : undefined;
}

export function publishAssistantMapContext(
  viewport: AssistantMapViewport,
  markers: PublishMarker[],
  selectedItemId?: string,
): AssistantMapContext {
  const selected = selectedItemId ? markers.find((marker) => marker.id === selectedItemId) : undefined;
  const visibleMarkers = markers.filter((marker) => visible(marker, viewport));
  const ordered = selected && !visibleMarkers.includes(selected)
    ? [selected, ...visibleMarkers]
    : selected
      ? [selected, ...visibleMarkers.filter((marker) => marker !== selected)]
      : visibleMarkers;
  const compactSelectedId = clean(selectedItemId, 64);

  latestMapContext = {
    viewport: {
      north: Number(viewport.north.toFixed(5)),
      south: Number(viewport.south.toFixed(5)),
      east: Number(viewport.east.toFixed(5)),
      west: Number(viewport.west.toFixed(5)),
      centerLatitude: Number(viewport.centerLatitude.toFixed(5)),
      centerLongitude: Number(viewport.centerLongitude.toFixed(5)),
      zoom: Number(viewport.zoom.toFixed(2)),
    },
    ...(compactSelectedId ? { selected_item_id: compactSelectedId } : {}),
    visible_items: ordered.slice(0, MAX_VISIBLE_ITEMS).map((marker) => {
      const id = clean(marker.id, 64);
      const kind = clean(marker.kind, 24);
      const name = clean(marker.label, 64);
      const meta = clean(marker.popupMeta, 64);
      return {
        ...(id ? { id } : {}),
        ...(kind ? { kind } : {}),
        ...(name ? { name } : {}),
        latitude: Number(marker.latitude.toFixed(5)),
        longitude: Number(marker.longitude.toFixed(5)),
        ...(meta ? { meta } : {}),
      };
    }),
    observed_at: new Date().toISOString(),
  };
  return latestMapContext;
}

export function currentAssistantMapContext(): AssistantMapContext | null {
  return latestMapContext;
}

export function clearAssistantMapContext(): void {
  latestMapContext = null;
}

export function focusAssistantMap(latitude: number, longitude: number, zoom = 14): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('doddledays:ai-map-focus', {
    detail: { latitude, longitude, zoom },
  }));
}

export function notifyAssistantDataChanged(tripSlug: string, tools: string[] = []): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<AssistantDataChangedDetail>('doddledays:ai-data-changed', {
    detail: { tripSlug, tools: tools.filter(Boolean).slice(0, 20) },
  }));
}
